<template>
  <div class="email-settings">
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <Mail :size="20" class="me-2" />
          Email Provider
        </h5>
      </div>
      <div class="card-body">
        <div class="d-flex gap-4">
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="emailProvider"
              id="providerSmtp"
              value="smtp"
              v-model="emailProvider"
            />
            <label class="form-check-label" for="providerSmtp">
              <Server :size="16" class="me-1" />
              SMTP Server
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="emailProvider"
              id="providerGraph"
              value="graph"
              v-model="emailProvider"
            />
            <label class="form-check-label" for="providerGraph">
              <i class="fab fa-microsoft me-1"></i>
              Microsoft Graph
            </label>
          </div>
        </div>
        <small class="text-muted mt-2 d-block">
          Select which email provider to use for sending emails.
        </small>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <Server :size="20" class="me-2" />
          SMTP Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loadState.IsLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveSettings">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">SMTP Host</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="smtpSettings.host"
                placeholder="smtp.example.com"
              />
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">SMTP Port</label>
              <input 
                type="number" 
                class="form-control" 
                v-model="smtpSettings.port"
                placeholder="587"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Username</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="smtpSettings.username"
                placeholder="username@example.com"
              />
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Password</label>
              <input 
                type="password" 
                class="form-control" 
                v-model="smtpSettings.password"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">From Email</label>
              <input 
                type="email" 
                class="form-control" 
                v-model="smtpSettings.fromEmail"
                placeholder="noreply@example.com"
              />
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">From Name</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="smtpSettings.fromName"
                placeholder="Company Name"
              />
            </div>
          </div>

          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              id="enableSsl" 
              v-model="smtpSettings.enableSsl"
            />
            <label class="form-check-label" for="enableSsl">
              Enable SSL/TLS
            </label>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary" @click="testEmail" :disabled="testState.IsLoading">
              <Send :size="16" class="me-1" />
              {{ testState.IsLoading ? 'Sending...' : 'Send Test Email' }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saveSmtpState.IsLoading">
              <Save :size="16" class="me-1" />
              {{ saveSmtpState.IsLoading ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    <!-- Microsoft Graph Configuration Card -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fab fa-microsoft me-2"></i>
          Microsoft Graph Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loadState.IsLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveGraphSettings">
          <div class="alert alert-info mb-3">
            <i class="fas fa-info-circle me-2"></i>
            To use Microsoft Graph for sending emails, you need to register an application in Azure AD
            and grant it the <code>Mail.Send</code> permission.
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Tenant ID <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="graphSettings.tenantId"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              <small class="text-muted">Azure AD Tenant ID (Directory ID)</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Client ID <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="graphSettings.clientId"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
              <small class="text-muted">Application (client) ID</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Client Secret <span class="text-danger">*</span></label>
              <input
                type="password"
                class="form-control"
                v-model="graphSettings.clientSecret"
                placeholder="••••••••"
              />
              <small class="text-muted">Application client secret</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">From Email <span class="text-danger">*</span></label>
              <input
                type="email"
                class="form-control"
                v-model="graphSettings.fromEmail"
                placeholder="noreply@yourdomain.com"
              />
              <small class="text-muted">Sender email address (must be a valid mailbox)</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">From Name</label>
              <input
                type="text"
                class="form-control"
                v-model="graphSettings.fromName"
                placeholder="Your Company Name"
              />
              <small class="text-muted">Display name for sender</small>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="submit" class="btn btn-primary" :disabled="saveGraphState.IsLoading">
              <Save :size="16" class="me-1" />
              {{ saveGraphState.IsLoading ? 'Saving...' : 'Save Graph Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { Mail, Server, Save, Send } from 'lucide-vue-next';
import OptionsService from '@/services/OptionsService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import ApiService from '@/services/ApiService';
import { useApiRequest } from '@/composables';

const { state: loadState, execute: executeLoad } = useApiRequest('email:load');
const { state: saveSmtpState, execute: executeSaveSmtp } = useApiRequest('email:saveSmtp');
const { state: saveGraphState, execute: executeSaveGraph } = useApiRequest('email:saveGraph');
const { state: testState, execute: executeTest } = useApiRequest('email:test');

const emailProvider = ref<'smtp' | 'graph'>('smtp');
const initialLoadComplete = ref(false);

const smtpSettings = ref({
  host: '',
  port: 587,
  username: '',
  password: '',
  fromEmail: '',
  fromName: '',
  enableSsl: true
});

const graphSettings = ref({
  tenantId: '',
  clientId: '',
  clientSecret: '',
  fromEmail: '',
  fromName: ''
});

const loadSettings = async () => {
  await executeLoad(async () => {
    const [provider, smtp, graph] = await Promise.all([
      OptionsService.getEmailProvider(),
      OptionsService.getSmtpSettings(),
      OptionsService.getGraphSettings()
    ]);
    return { provider, smtp, graph };
  });

  if (loadState.value.IsError) {
    Alert.notify({ text: 'Failed to load email settings', type: 'error' });
    return;
  }

  const { provider, smtp, graph } = loadState.value.Data;
  emailProvider.value = provider;
  smtpSettings.value = { ...smtp };
  graphSettings.value = { ...graph };
  initialLoadComplete.value = true;
};

const saveSettings = async () => {
  await executeSaveSmtp(() => OptionsService.saveSmtpSettings({ ...smtpSettings.value }));
  if (saveSmtpState.value.IsError) {
    Alert.notify({ text: 'Failed to save email settings', type: 'error' });
  } else {
    Toast.success('SMTP settings saved successfully!');
  }
};

const saveGraphSettings = async () => {
  await executeSaveGraph(() => OptionsService.saveGraphSettings({ ...graphSettings.value }));
  if (saveGraphState.value.IsError) {
    Alert.notify({ text: 'Failed to save Graph settings', type: 'error' });
  } else {
    Toast.success('Microsoft Graph settings saved successfully!');
  }
};

const testEmail = async () => {
  await executeTest(() => ApiService.testEmail());
  if (testState.value.IsError) {
    Alert.notify({ text: 'Failed to send test email', type: 'error' });
  } else {
    Toast.success('Test email sent successfully!');
  }
};

// Auto-save email provider when changed (after initial load)
watch(emailProvider, async (newValue, oldValue) => {
  if (!initialLoadComplete.value) return;
  if (newValue !== oldValue) {
    try {
      await OptionsService.saveEmailProvider(newValue);
      Toast.success(`Email provider changed to ${newValue === 'smtp' ? 'SMTP' : 'Microsoft Graph'}`);
    } catch (error) {
      console.error('Error saving email provider:', error);
    }
  }
});

onMounted(() => {
  loadSettings();
});
</script>
